<template>
  <div id='outlets-content'>
    <app-list
      :list-type=listType
      :fields=searchFields
      :records=editorOutletsData
      :records-pagination="outletsPagination"
      :isListBusy="outletsIsBusy"
      :displayColumns=displayColumns
      :allowAdd="false"
      :allowAdvancedSearch="true"
      :searchModel="searchModel"
      :isItemClickable="true"
      :showHeaders="true"
      :sortableHeaders="true"
      :activeRecord="activeOutlet"
      @updateSearchModel="updateSearchModel"
      @onSearchSubmit="onSearchSubmit"
      @onListAddClick="onListAddClick"
      @onRecordClick="onOutletRecordClick"
    />

    <app-form-modal
        v-if="showNewOutletModal"
        title="outlets.add_outlet_header"
        :fields="['title', 'url', 'media_type', 'country', 'region']"
        :fieldsAdditions="addFieldsAdditions"
        @onSave="submitNewOutlet"
        @closeModal="closeNewOutletModal"
    />

  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import List from './list';
import FormModal from '../form-modal';

import { FIELD_TYPE, VALIDATIONS } from '../../config/const';

export default {
  props: {
    activeOutlet: {
      type: Object,
      default: () => { return undefined }
    }
  },
  components: {
    'app-list': List,
    'app-form-modal': FormModal
  },
  computed: {
    ...mapState('editorOutlet', [
      'editorOutlet'
    ]),
    ...mapState('editorOutlets', [
      'editorOutlets'
    ]),
    ...mapState('mediaTypes', [
      'mediaTypes'
    ]),
    ...mapState('editorCountries', [
      'editorCountries'
    ]),
    ...mapState('regions', [
      'regions'
    ]),
    editorOutletsData() { return this.editorOutlets && this.editorOutlets.data && this.editorOutlets.data.data },
    outletsPagination() { return this.editorOutlets && this.editorOutlets.data && this.editorOutlets.data.pagination },
    outletsIsBusy() { return this.editorOutlets && this.editorOutlets.isBusy },
    projectId() {
      return this.$route.params.project_id;
    },
    mediaTypesWithLabels () {
      return this.mediaTypes.data && this.mediaTypes.data.map( x => ({ 
        label: this.$i18n.t(`outlets.media_types.${x.mediatype.replace("-","_")}`), 
        value: x.mediatype 
      })) || [] ;
    },
    searchFields () {
      return {
        title: {
          type: FIELD_TYPE.INPUT,
          label: this.$i18n.t('outlets.title'),
          id: this.listType + '-search-title',
          name: 'title',
          style: 'flex-basis: 100%',
        },
        url: {
          type: FIELD_TYPE.INPUT,
          label: this.$i18n.t('outlets.url'),
          id: this.listType + '-search-url',
          name: 'url',
          style: 'flex-basis: 100%',
          validate: [VALIDATIONS.URL_FORMAT]
        },
        media_type: {
          type: FIELD_TYPE.SELECT,
          label: this.$i18n.t('outlets.media_type.title'),
          id: this.listType + '-search-format',
          name: 'media_type',
          options: this.mediaTypesWithLabels, 
          searchable: true,
          optionsLimit: 100,
          dropdownDirection: 'bottom',
          placeholder: this.$i18n.t('outlets.media_type.select'),
          hideSelected: false,
          style: 'flex-basis: 100%',
          customLabel: this.displayLabel,
          trackBy: 'value'
        },
        country: {
          type: FIELD_TYPE.SELECT,
          label: this.$i18n.t('outlets.country.title'),
          id: this.listType + '-search-country',
          name: 'country',
          options: this.editorCountries && this.editorCountries.data || [],
          searchable: true,
          optionsLimit: 100,
          dropdownDirection: 'bottom',
          placeholder: this.$i18n.t('outlets.country.select'),
          hideSelected: false,
          style: 'flex-grow: 1',
          customClass: 'mr10',
          customLabel: this.displayLabel,
          trackBy: 'value'
        },
        region: {
          type: FIELD_TYPE.SELECT,
          label: this.$i18n.t('outlets.region.title'),
          id: this.listType + '-search-region',
          name: 'region',
          options: this.regions && this.regions.data || [],
          searchable: true,
          optionsLimit: 100,
          dropdownDirection: 'bottom',
          placeholder: this.$i18n.t('outlets.region.select'),
          hideSelected: false,
          style: 'flex-grow: 1',
          customLabel: this.displayLabel,
          trackBy: 'value'
        }
      }
    },
    addFieldsAdditions () {
      return {
        media_type: {
          dropdownDirection: 'top',
          options: this.mediaTypes && this.mediaTypes.data || []
        },
        country: {
          dropdownDirection: 'top',
          options: this.editorCountries && this.editorCountries.data || []
        },
        region: {
          dropdownDirection: 'top',
          options: this.regions && this.regions.data || []
        }
      }
    }
  },
  mounted () {
    this.loadOutlets();

    if (!this.mediaTypes.isBusy) {
      this.getMediaTypes({
        projectId: this.projectId
      });
    }

    if (!this.editorCountries.isBusy) {
      this.getCountries({
        projectId: this.projectId
      });
    }

    if (!this.regions.isBusy) {
      this.getRegions({
        projectId: this.projectId
      });
    }
  },
  watch: {
    "editorOutlet.isBusy": {
      handler (newValue, oldValue) {
        if (oldValue && !newValue) {
          this.onSearchSubmit();
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions({
      'addOutlet': 'editorOutlet/addOutlet',
      'getOutlets': 'editorOutlets/getOutlets',
      'getMediaTypes': 'mediaTypes/getMediaTypes',
      'getCountries': 'editorCountries/getCountries',
      'getRegions': 'regions/getRegions'
    }),
    loadOutlets (filterValues = {}, pagination = {}) {
      if (!this.editorOutlets.isBusy) {
        if (!pagination.order) {
          pagination.order = this.defaultOrder;
          pagination.sort = 'asc';
        }

        this.getOutlets({
          projectId: this.projectId,
          filterValues: filterValues,
          pagination: pagination
        });
      }
    },
    displayLabel (option) {
      return `${option.label}`
    },
    updateSearchModel (model) {
      this.searchModel = model;
    },
    onSearchSubmit (pagination = {}) {
      const filterValues = this.searchModel.searchQuery ? {} : this.searchModel;
      if(this.searchModel.searchQuery) {
        pagination.query = this.searchModel.searchQuery;
      }

      this.loadOutlets(filterValues, pagination);
    },
    onListAddClick (e) {
      if(e) {
        e.preventDefault();
        e.stopPropagation();
      }

      this.showNewOutletModal = true;
    },
    closeNewOutletModal () {
      this.showNewOutletModal = false;
    },
    submitNewOutlet (outlet) {
      if (!this.editorOutlet.isBusy) {
        this.addOutlet({
          projectId: this.projectId,
          outlet
        });
      }
      this.closeNewOutletModal();
    },
    onOutletRecordClick (outlet) {
      this.$emit('saveArticleOutlet', outlet.id)
    }
  },
  data () {
    return {
      listType: 'outlets',
      displayColumns: [
        { label: 'Name', value: 'name', width: '35%' },
        { label: 'Media Type', value: 'media_type', width: '30%' },
        { label: 'Link', value: 'url', width: '35%' }
      ],
      showNewOutletModal: false,
      searchModel: {},
      pagination: {},
      defaultOrder: 'name'
    }
  }
}
</script>